/**
 * Transforms a Cloudinary URL to use optimized delivery parameters.
 * Non-Cloudinary URLs are returned unchanged.
 *
 * @param {string} url - The image URL
 * @param {object} opts
 * @param {number} [opts.width]  - Desired width (default 800)
 * @param {number} [opts.height] - Desired height (omit to let Cloudinary auto-calculate)
 * @param {string} [opts.crop]   - Crop mode: 'fill', 'limit', 'fit' etc. (default 'limit')
 * @param {string} [opts.gravity] - Crop gravity: 'auto', 'face', 'center' (only with c_fill)
 * @param {string} [opts.quality] - Quality: 'auto', 'auto:low', 'auto:good' (default 'auto')
 * @param {number} [opts.dpr]    - Device pixel ratio multiplier for retina (default 1)
 * @returns {string} Optimized URL
 */
export function optimizeCloudinaryUrl(url, opts = {}) {
  if (!url || typeof url !== 'string') return url;
  if (!url.includes('res.cloudinary.com') || !url.includes('/upload/')) return url;

  const { width = 800, height, crop = 'limit', gravity, quality = 'auto', dpr } = opts;

  // Build transformation string
  const parts = [`f_auto`, `q_${quality}`, `c_${crop}`];
  if (width) parts.push(`w_${width}`);
  if (height) parts.push(`h_${height}`);
  if (gravity) parts.push(`g_${gravity}`);
  if (dpr && dpr !== 1) parts.push(`dpr_${dpr}`);
  const transform = parts.join(',');

  // Avoid double-transforming – if the URL already has transforms after /upload/
  const uploadIdx = url.indexOf('/upload/');
  const afterUpload = url.substring(uploadIdx + 8);
  if (afterUpload.match(/^[a-z]_/)) return url;

  return url.replace('/upload/', `/upload/${transform}/`);
}

/**
 * Build a `srcSet` string with 1x and 2x variants at the same aspect ratio.
 * Used to deliver retina-crisp cropped images on cards and hero banners.
 */
export function buildSrcSet(url, { width, height, crop = 'fill', gravity = 'auto' } = {}) {
  if (!url || typeof url !== 'string' || !url.includes('res.cloudinary.com')) return undefined;
  const v1 = optimizeCloudinaryUrl(url, { width, height, crop, gravity });
  const v2 = optimizeCloudinaryUrl(url, { width: width * 2, height: height ? height * 2 : undefined, crop, gravity });
  return `${v1} 1x, ${v2} 2x`;
}

/**
 * Card thumbnail — content-aware 4:3 crop at 500px wide (1000px for retina).
 * Use this as the `src`; pair with `cardThumbnailSrcSet()` for `srcSet`.
 */
export function cardThumbnail(url) {
  return optimizeCloudinaryUrl(url, { width: 500, height: 375, crop: 'fill', gravity: 'auto' });
}

export function cardThumbnailSrcSet(url) {
  return buildSrcSet(url, { width: 500, height: 375 });
}

/**
 * Square card thumbnail — for marketplace/classified 1:1 cards.
 */
export function cardThumbnailSquare(url) {
  return optimizeCloudinaryUrl(url, { width: 500, height: 500, crop: 'fill', gravity: 'auto' });
}

export function cardThumbnailSquareSrcSet(url) {
  return buildSrcSet(url, { width: 500, height: 500 });
}

/**
 * Detail page hero — 16:9 crop at 1600px wide.
 * Content-aware gravity prevents cropping off faces / focal content.
 */
export function detailHero(url) {
  return optimizeCloudinaryUrl(url, { width: 1600, height: 900, crop: 'fill', gravity: 'auto' });
}

export function detailHeroSrcSet(url) {
  return buildSrcSet(url, { width: 1600, height: 900 });
}

/**
 * Detail page image (legacy) — 800px wide without fixed height.
 * Retained for the image lightbox / fallback scenarios.
 */
export function detailImage(url) {
  return optimizeCloudinaryUrl(url, { width: 800 });
}

/**
 * Hero/full-width images — 1200px wide, no crop.
 */
export function heroImage(url) {
  return optimizeCloudinaryUrl(url, { width: 1200 });
}

/**
 * Small square avatar / user photo — face-aware crop.
 * Default 96px, which comfortably covers up to 48px rendered @ 2x DPR.
 * Pass `size` (in physical pixels served) if you need something different;
 * for a 64px rendered avatar on retina, pass 128.
 */
export function avatarThumbnail(url, size = 96) {
  return optimizeCloudinaryUrl(url, {
    width: size,
    height: size,
    crop: 'fill',
    gravity: 'face',
  });
}

/**
 * Tiny inline thumbnail (list rows, sidebar cards). ~112px covers up to
 * 56px rendered @ 2x DPR. Content-aware, not face-focused.
 */
export function tinyThumbnail(url, size = 112) {
  return optimizeCloudinaryUrl(url, {
    width: size,
    height: size,
    crop: 'fill',
    gravity: 'auto',
  });
}
